import { z } from "zod";

const datePattern = /^\d{4}-\d{2}-\d{2}$/;
const monthPattern = /^\d{4}-\d{2}$/;
const offsetDateTimePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?(?:\.\d{3})?(?:Z|[+-]\d{2}:\d{2})$/;

const validDate = (value: string) => {
  if (!datePattern.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year!, month! - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month! - 1 && date.getUTCDate() === day;
};

export const businessDateSchema = z.string().refine(validDate, "営業日はYYYY-MM-DD形式の実在する日付にしてください");
export const monthSchema = z.string().regex(monthPattern, "月はYYYY-MM形式で指定してください").refine(
  (value) => {
    const month = Number(value.slice(5, 7));
    return month >= 1 && month <= 12;
  },
  "実在する月を指定してください",
);
export const uuidSchema = z.string().uuid("UUID形式で指定してください");
export const yenSchema = z.number().int("金額は円単位の整数にしてください").min(0, "金額は0円以上にしてください");
export const positiveYenSchema = yenSchema.min(1, "金額は1円以上にしてください");

const optionalLineId = z.string().min(1).max(80).optional();
const optionalMemo = (max: number) => z.string().trim().max(max).optional().default("");

export const customerSchema = z.object({
  lineId: optionalLineId,
  customerLabel: z.string().trim().min(1, "顧客名を入力してください").max(80),
  amount: positiveYenSchema,
  memo: optionalMemo(200),
});

export const receivableSchema = z.object({
  lineId: optionalLineId,
  receivableId: z.string().min(1).max(80).optional(),
  type: z.enum(["TAB", "TRANSFER_DUE"]),
  customerLabel: z.string().trim().min(1, "未収の対象を入力してください").max(80),
  amount: positiveYenSchema,
  description: optionalMemo(300),
});

export const expenseSchema = z.object({
  lineId: optionalLineId,
  vendorName: z.string().trim().min(1, "支払先を入力してください").max(100),
  amount: positiveYenSchema,
  paymentMethod: z.enum(["CASH", "PAYPAY", "CARD", "BANK", "OTHER"]),
  memo: optionalMemo(300),
});

export const shiftSchema = z.object({
  lineId: optionalLineId,
  staffId: z.string().trim().min(1).max(80),
  staffName: z.string().trim().max(100).optional(),
  startedAt: z.string().regex(offsetDateTimePattern, "開始日時にはタイムゾーンを含めてください"),
  endedAt: z.string().regex(offsetDateTimePattern, "終了日時にはタイムゾーンを含めてください"),
  breakMinutes: z.number().int().min(0).max(1_439),
  baseHourlyRateSnapshot: positiveYenSchema.optional(),
  paidTodayMethod: z.enum(["UNPAID", "CASH", "OTHER"]),
});

export const collectionSchema = z.object({
  lineId: optionalLineId,
  collectionId: z.string().min(1).max(80).optional(),
  receivableId: z.string().trim().min(1, "回収対象を選択してください").max(80),
  amount: positiveYenSchema,
  receivedMethod: z.enum(["CASH", "PAYPAY", "CARD", "BANK"]),
  description: optionalMemo(300),
});

export const dailyPayloadSchema = z.object({
  requestId: uuidSchema,
  expectedRevision: z.number().int().min(0),
  businessDate: businessDateSchema,
  openingRegisterCash: yenSchema.nullable().default(null),
  closingRegisterCashActual: yenSchema.nullable().default(null),
  customers: z.array(customerSchema).max(100),
  payments: z.object({
    paypay: yenSchema,
    card: yenSchema,
    tab: yenSchema,
    transferDue: yenSchema,
  }),
  receivables: z.array(receivableSchema).max(100),
  expenses: z.array(expenseSchema).max(100),
  shifts: z.array(shiftSchema).max(100),
  collections: z.array(collectionSchema).max(100),
  note: z.string().trim().max(1_000),
});

export const historyQuerySchema = z.object({
  from: businessDateSchema.optional(),
  to: businessDateSchema.optional(),
});

export const monthlyQuerySchema = z.object({ month: monthSchema });
